import React, { useState, useEffect, useCallback } from "react";
import { Plus } from "lucide-react";
import { ApiService } from "../../../services/api";
import { MeetingParticipant, User as UserType } from "../../../types/meeting";
import checkPermission from "../../../utils/permission";
import { useModal } from "../../../hooks/useModal";
import ParticipantList from "./ParticipantList";
import AddParticipantModal from "../../../components/modals/AddParticipantModal";

interface ParticipantManagerProps {
  meetingId: string;
  onComplete?: () => void;
}

export default function ParticipantManager({ meetingId, onComplete }: ParticipantManagerProps) {
  const { showAlert, showConfirm } = useModal();
  const [participants, setParticipants] = useState<MeetingParticipant[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const canUpdateMeeting = checkPermission("meet_update");

  const buildDepartmentHierarchy = (departmentParticipants: MeetingParticipant[]) => {
    const departmentMap = new Map();

    departmentParticipants.forEach((participant) => {
      if (participant.department) {
        departmentMap.set(participant.department.id, {
          ...participant,
          department: {
            ...participant.department,
            parentId: (participant.department as any)?.parentId || null,
          },
        });
      }
    });

    const availableParentIds = new Set(Array.from(departmentMap.keys()));

    const buildTree = (parentId: string | null = null, level: number = 0): any[] => {
      const children: any[] = [];

      for (const [, participant] of departmentMap) {
        const dept = participant.department;

        const isActualRoot = !dept.parentId || dept.parentId === null || dept.parentId === "";
        const isOrphanedDepartment = dept.parentId && !availableParentIds.has(dept.parentId);
        const isMatchingParent = dept.parentId === parentId;

        if ((parentId === null && (isActualRoot || isOrphanedDepartment)) || isMatchingParent) {
          children.push({
            ...participant,
            level,
            children: buildTree(dept.id, level + 1),
          });
        }
      }

      return children.sort((a, b) =>
        (a.department?.name || "").localeCompare(b.department?.name || "", "tr", {
          sensitivity: "accent",
        })
      );
    };

    const flatten = (nodes: any[]): any[] => {
      let result: any[] = [];
      nodes.forEach((node) => {
        result.push(node);
        if (node.children && node.children.length > 0) {
          result = result.concat(flatten(node.children));
        }
      });
      return result;
    };

    const tree = buildTree();

    if (tree.length === 0 && departmentParticipants.length > 0) {
      return departmentParticipants.map((participant, index) => ({
        ...participant,
        level: 0,
      }));
    }

    return flatten(tree);
  };

  const parseApiResponse = (response: any, dataKey?: string): any[] => {
    if (Array.isArray(response)) {
      return response;
    }

    if (!response.data) {
      return [];
    }

    if (dataKey && response.data[dataKey]) {
      return response.data[dataKey];
    } else if (response.data.data) {
      return response.data.data;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  };

  const loadParticipants = useCallback(async () => {
    try {
      const response: any = await ApiService.getMeetingParticipants(meetingId);
      const participants = parseApiResponse(response);
      setParticipants(participants);
    } catch (error: any) {
      if (error.message && error.message.includes("Meet not found")) {
        setParticipants([]);
        return;
      }
      await showAlert({
        title: "Hata",
        message: error.message || "Katılımcılar yüklenirken hata oluştu.",
        type: "error",
      });
    }
  }, [meetingId, showAlert]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      let participantsResponse = null;
      try {
        participantsResponse = await ApiService.getMeetingParticipants(meetingId);
      } catch (participantsError: any) {
        if (participantsError.message && participantsError.message.includes("Meet not found")) {
          participantsResponse = { data: [] };
        } else {
          throw participantsError;
        }
      }

      const participants = parseApiResponse(participantsResponse);
      setParticipants(participants);
    } catch (error: any) {
      if (error.message && error.message.includes("Meet not found")) {
        setParticipants([]);
        return;
      }
      await showAlert({
        title: "Hata",
        message: error.message || "Veriler yüklenirken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [meetingId, showAlert]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddParticipant = async (formData: any) => {
    if (!canUpdateMeeting) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Toplantı güncelleme yetkiniz bulunmamaktadır.",
        type: "error",
      });
      return;
    }

    setAddLoading(true);

    try {
      if (formData.type === "USER") {
        for (const userId of formData.selectedUsers) {
          await ApiService.createMeetingParticipant(meetingId, {
            type: "USER",
            userId: userId,
          });
        }
      } else {
        for (const departmentId of formData.selectedDepartments) {
          await ApiService.createMeetingParticipant(meetingId, {
            type: "DEPARTMENT",
            departmentId: departmentId,
          });
        }
      }

      setAddModalOpen(false);
      await loadParticipants();
    } catch (error: any) {
      console.error("Katılımcı ekleme hatası:", error);
      await showAlert({
        title: "Hata",
        message: error.message || "Katılımcı eklenirken hata oluştu.",
        type: "error",
      });
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteParticipant = async (participant: MeetingParticipant) => {
    if (!canUpdateMeeting) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Toplantı güncelleme yetkiniz bulunmamaktadır.",
        type: "error",
      });
      return;
    }

    const confirmed = await showConfirm({
      title: "Katılımcıyı Çıkar",
      message: `"${
        participant.type === "USER"
          ? `${participant.user?.firstName || ""} ${participant.user?.lastName || ""}`.trim() || "İsimsiz Kullanıcı"
          : participant.department?.name || "İsimsiz Departman"
      }" katılımcısını çıkarmak istediğinizden emin misiniz?`,
      confirmText: "Çıkar",
      confirmButtonVariant: "danger",
    });

    if (confirmed) {
      setDeleteLoading(participant.id);

      try {
        await ApiService.deleteMeetingParticipant(meetingId, participant.id);
        await loadParticipants();
      } catch (error: any) {
        console.error("Katılımcı silme hatası:", error);
        await showAlert({
          title: "Hata",
          message: error.message || "Katılımcı çıkarılırken hata oluştu.",
          type: "error",
        });
      } finally {
        setDeleteLoading(null);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        {canUpdateMeeting && (
          <button
            onClick={() => setAddModalOpen(true)}
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-1">
            <Plus className="w-4 h-4" />
            Katılımcı Ekle
          </button>
        )}
      </div>

      <ParticipantList
        participants={participants}
        canUpdateMeeting={canUpdateMeeting}
        deleteLoading={deleteLoading}
        onDeleteParticipant={handleDeleteParticipant}
        buildDepartmentHierarchy={buildDepartmentHierarchy}
      />

      {onComplete && (
        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
          <button onClick={onComplete} className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm">
            Tamamla
          </button>
        </div>
      )}

      <AddParticipantModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSubmit={handleAddParticipant}
        isLoading={addLoading}
        title="Toplantıya Katılımcı Ekle"
        excludeUserIds={participants.filter((p) => p.type === "USER" && p.user?.id).map((p) => p.user!.id)}
        excludeDepartmentIds={participants.filter((p) => p.type === "DEPARTMENT" && p.department?.id).map((p) => p.department!.id)}
      />
    </div>
  );
}
